// src/i18n/i18n.ts
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

type I18nAdditionalOptions = {
  phrase?: {
    projectId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export const defaultLocale = 'en';
export const defaultNamespace = 'translations';

export const options = {
  fallbackLng: defaultLocale,
  ns: [defaultNamespace],
  defaultNS: defaultNamespace,
  debug: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  initAsync: false, // Needed for SSR to work
};

export const ice = {
  fallbackLng: defaultLocale,
  ns: [defaultNamespace],
  defaultNS: defaultNamespace,
  debug: false,
  interpolation: {
    escapeValue: false,
    prefix: '{{',
    suffix: '}}',
  },
  postProcess: ['phraseInContextEditor'],
  react: {
    useSuspense: false,
  },
  initAsync: false, // Needed for SSR to work
  fullReparse: true,
  autoLowercase: false,
};

const isPhraseEnabled = () =>
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).has('editPhrase')
    : false;

/**
 * Factory that initializes i18next and returns the instance.
 * - Loads translations synchronously from translations/translations.json
 *   so SSR renders with resources available.
 * - Optionally wires the phrase post-processor if projectId + editPhrase is present.
 *
 * additionalOptions: { phrase: { projectId?: string } }
 */
export default (additionalOptions: I18nAdditionalOptions = {}) => {
  const { phrase = {} } = additionalOptions;
  const { projectId } = phrase;

  // Load translations JSON synchronously so SSR has resources.
  let translationsResource: Record<string, unknown> = {};
  try {
    // NOTE: this path expects your file at:
    // <project-root>/translations/translations.json
    // From src/i18n this goes up two levels to project root.
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    translationsResource = require('../../translations/translations.json');
  } catch (err) {
    // If the file isn't found, keep an empty resource object.
    // console.warn('[i18n] translations file not found', err);
    translationsResource = {};
  }

  // Base init options (merge resources)
  const baseInitOptions = {
    resources: {
      en: {
        // namespace name must match the file name 'translations.json'
        translations: translationsResource || {},
      },
    },
    lng: defaultLocale,
    fallbackLng: defaultLocale,
    ns: [defaultNamespace],
    defaultNS: defaultNamespace,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    initAsync: false,
  };

  // If phrase editing is enabled and projectId provided, attempt to load post-processor
  if (projectId && typeof window !== 'undefined' && isPhraseEnabled()) {
    try {
      // dynamic require of the post-processor - only in browser
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const PhraseInContextEditorPostProcessor = require('i18next-phrase-in-context-editor-post-processor');

      const instance =
        typeof PhraseInContextEditorPostProcessor === 'function'
          ? new PhraseInContextEditorPostProcessor({
              autoLowercase: false,
              phraseEnabled: true,
              projectId,
            })
          : PhraseInContextEditorPostProcessor;

      i18n
        .use(instance)
        .use(initReactI18next)
        .init({
          ...ice,
          ...baseInitOptions,
        });
    } catch (err) {
      // Fallback to regular init if post-processor cannot be loaded
      // eslint-disable-next-line no-console
      console.warn(
        '[i18n] Phrase post-processor not available. Falling back to regular i18n.',
        err
      );
      i18n.use(initReactI18next).init({
        ...options,
        ...baseInitOptions,
      });
    }
  } else {
    // Normal init (server & client)
    i18n.use(initReactI18next).init({
      ...options,
      ...baseInitOptions,
    });
  }

  // Optional: attach to window for debugging (safe-guarded)
  if (typeof window !== 'undefined') {
    // @ts-ignore
    // eslint-disable-next-line no-param-reassign
    window.i18next = i18n;
  }

  return i18n;
};
